import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ScrollArea } from './ui/scroll-area';
import { Loader2, Activity, MessageSquare, MousePointer, Brain, Target, Clock, TrendingUp, ExternalLink, RefreshCw } from 'lucide-react';
import { io, Socket } from 'socket.io-client';
import { format } from 'date-fns';
import { SOCKET_URL } from '../config/api';

interface SessionMonitorProps {
  sessionId: string;
  onClose?: () => void;
}

interface SessionData {
  session: {
    sessionId: string;
    openReplaySessionId?: string;
    siteName?: string;
    siteUrl?: string;
    createdAt: string;
    updatedAt: string;
  };
  metrics: {
    duration: number;
    pageCount: number;
    chatInteractions: number;
    trackedEvents: number;
    highIntentEvents: number;
    agentCalls: number;
    averageConfidence: number;
  };
  currentState: {
    latestIntent: any;
    currentPage: any;
    activeRoles: any[];
    recentEvents: any[];
  };
  pages: any[];
  chatInteractions: any[];
  trackedEvents: any[];
  agentActivity: any[];
  intentHistory: any[];
  timeline: any[];
}

export default function SessionMonitor({ sessionId, onClose }: SessionMonitorProps) {
  const [loading, setLoading] = useState(true);
  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const timelineRef = useRef<HTMLDivElement>(null);

  // Fetch initial session data
  const fetchSessionData = async () => {
    try {
      const response = await fetch(`/api/session-monitor/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch session data');
      
      const data = await response.json();
      setSessionData(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching session data:', error);
      setLoading(false);
    }
  };

  // Set up WebSocket connection for real-time updates
  useEffect(() => {
    fetchSessionData();

    // Connect to WebSocket through proxy in development or when SOCKET_URL is empty
    const socketUrl = import.meta.env.DEV || SOCKET_URL === '' ? '' : SOCKET_URL;
    
    // Skip WebSocket connection if URL contains localhost and we're not in development
    if (!import.meta.env.DEV && SOCKET_URL.includes('localhost')) {
      console.warn('Skipping WebSocket connection - invalid URL for non-development environment:', SOCKET_URL);
      setLoading(false);
      return;
    }
    
    const socket = io(socketUrl, {
      path: '/socket.io/',
      query: { sessionId },
      transports: ['websocket', 'polling']
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('Connected to session monitor');
      setIsConnected(true);
      socket.emit('monitor-session', sessionId);
    });

    socket.on('disconnect', () => {
      console.log('Disconnected from session monitor');
      setIsConnected(false);
    });

    socket.on('session-update', (update: any) => {
      console.log('Received session update:', update);
      
      // Update session data based on the update type
      setSessionData(prev => {
        if (!prev) return prev;
        
        const newData = { ...prev };
        
        // Add to timeline
        newData.timeline = [{
          type: update.type,
          timestamp: update.timestamp,
          data: update.data
        }, ...newData.timeline].slice(0, 100);
        
        // Update specific sections based on update type
        if (update.type === 'customer-intent') {
          newData.currentState.latestIntent = update.data;
          newData.intentHistory = [update.data, ...newData.intentHistory].slice(0, 50);
          newData.metrics.averageConfidence = update.data.confidence || newData.metrics.averageConfidence;
        } else if (update.type === 'chat-message') {
          newData.chatInteractions = [update.data, ...newData.chatInteractions].slice(0, 50);
          newData.metrics.chatInteractions++;
        } else if (update.type === 'tracked-event') {
          newData.trackedEvents = [update.data, ...newData.trackedEvents].slice(0, 50);
          newData.metrics.trackedEvents++;
          if (update.data.intentLevel > 80) {
            newData.metrics.highIntentEvents++;
          }
        }
        
        return newData;
      });
    });

    return () => {
      socket.emit('stop-monitoring', sessionId);
      socket.disconnect();
    };
  }, [sessionId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!sessionData) {
    return (
      <div className="text-center p-8">
        <p className="text-muted-foreground">Failed to load session data</p>
      </div>
    );
  }

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="border-b p-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              Session Monitor
              <Badge variant={isConnected ? "default" : "secondary"}>
                {isConnected ? "Live" : "Offline"}
              </Badge>
            </h2>
            <p className="text-muted-foreground">
              {sessionData.session.siteName} • Session: {sessionId.substring(0, 12)}...
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchSessionData}
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
            {sessionData.session.openReplaySessionId && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(`/session-replays?sessionId=${sessionId}`, '_blank')}
              >
                <ExternalLink className="h-4 w-4 mr-1" />
                View Replay
              </Button>
            )}
            {onClose && (
              <Button variant="ghost" size="sm" onClick={onClose}>
                Close
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Metrics Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 p-4 border-b">
        <div className="text-center">
          <p className="text-2xl font-bold">{formatDuration(sessionData.metrics.duration)}</p>
          <p className="text-xs text-muted-foreground">Duration</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold">{sessionData.metrics.pageCount}</p>
          <p className="text-xs text-muted-foreground">Pages</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold">{sessionData.metrics.chatInteractions}</p>
          <p className="text-xs text-muted-foreground">Chats</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold">{sessionData.metrics.trackedEvents}</p>
          <p className="text-xs text-muted-foreground">Events</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold">{sessionData.metrics.highIntentEvents}</p>
          <p className="text-xs text-muted-foreground">High Intent</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold">{sessionData.metrics.agentCalls}</p>
          <p className="text-xs text-muted-foreground">AI Calls</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold">{Math.round(sessionData.metrics.averageConfidence * 100)}%</p>
          <p className="text-xs text-muted-foreground">Confidence</p>
        </div>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <TabsList className="mx-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="intents">Customer Intent</TabsTrigger>
          <TabsTrigger value="events">Events</TabsTrigger>
          <TabsTrigger value="chat">Chat History</TabsTrigger>
          <TabsTrigger value="pages">Pages</TabsTrigger>
          <TabsTrigger value="agents">AI Activity</TabsTrigger>
        </TabsList>

        <div className="flex-1 overflow-hidden">
          <TabsContent value="overview" className="h-full p-4">
            <div className="grid gap-4">
              {/* Current State */}
              <Card>
                <CardHeader>
                  <CardTitle>Current State</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {sessionData.currentState.currentPage && (
                      <div>
                        <p className="text-sm font-medium mb-1">Current Page</p>
                        <p className="text-sm text-muted-foreground">{sessionData.currentState.currentPage.url}</p>
                        {sessionData.currentState.activeRoles.length > 0 && (
                          <div className="flex gap-2 mt-2">
                            {sessionData.currentState.activeRoles.map((role: any, i: number) => (
                              <Badge key={i} variant="secondary">
                                {role.role} ({Math.round(role.confidence * 100)}%)
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {sessionData.currentState.latestIntent && (
                      <div>
                        <p className="text-sm font-medium mb-1">Latest Intent</p>
                        <div className="flex items-center gap-2">
                          <Badge>{sessionData.currentState.latestIntent.primaryIntent}</Badge>
                          <span className="text-sm text-muted-foreground">
                            {sessionData.currentState.latestIntent.sentiment?.overall || 'neutral'} • 
                            {sessionData.currentState.latestIntent.urgency?.level || 'normal'}
                          </span>
                        </div>
                        {sessionData.currentState.latestIntent.whatISee && (
                          <p className="text-sm text-muted-foreground mt-2">
                            {sessionData.currentState.latestIntent.whatISee}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Events */}
              {sessionData.currentState.recentEvents.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Recent High-Value Events</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {sessionData.currentState.recentEvents.map((event: any, i: number) => (
                        <div key={i} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <MousePointer className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">{event.event_name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant={event.intent_level > 80 ? "destructive" : "secondary"}>
                              Intent: {event.intent_level}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {format(new Date(event.timestamp), 'HH:mm:ss')}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="timeline" className="h-full">
            <ScrollArea className="h-full p-4">
              <div className="space-y-4" ref={timelineRef}>
                {sessionData.timeline.map((item: any, i: number) => (
                  <div key={i} className="flex gap-4">
                    <div className="flex-shrink-0 w-32 text-right">
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(item.timestamp), 'HH:mm:ss.SSS')}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <div className={`w-3 h-3 rounded-full ${
                        item.type === 'page_visit' ? 'bg-blue-500' :
                        item.type === 'chat_message' ? 'bg-green-500' :
                        item.type === 'tracked_event' ? 'bg-orange-500' :
                        item.type === 'intent_update' ? 'bg-purple-500' :
                        'bg-gray-500'
                      }`} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{item.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                      <div className="text-sm text-muted-foreground">
                        {item.type === 'page_visit' && (
                          <div>
                            <p>{item.data.title || 'Untitled'}</p>
                            <p className="text-xs">{item.data.url}</p>
                          </div>
                        )}
                        {item.type === 'chat_message' && (
                          <div>
                            <p>User: {item.data.message}</p>
                            <p className="mt-1">Bot: {item.data.reply?.substring(0, 100)}...</p>
                          </div>
                        )}
                        {item.type === 'tracked_event' && (
                          <p>{item.data.eventName} • {item.data.action} • Intent: {item.data.intentLevel}</p>
                        )}
                        {item.type === 'intent_update' && (
                          <div>
                            <p>Intent: {item.data.intent} ({Math.round(item.data.confidence * 100)}%)</p>
                            <p>Sentiment: {item.data.sentiment} • Urgency: {item.data.urgency}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="intents" className="h-full p-4">
            <ScrollArea className="h-full">
              <div className="space-y-4">
                {sessionData.intentHistory.map((intent: any, i: number) => (
                  <Card key={i}>
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <Badge className="mb-2">{intent.intent || intent.primaryIntent}</Badge>
                          <p className="text-sm text-muted-foreground">
                            {format(new Date(intent.created_at), 'HH:mm:ss')}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold">{Math.round((intent.confidence || 0) * 100)}%</p>
                          <p className="text-xs text-muted-foreground">Confidence</p>
                        </div>
                      </div>
                      
                      {intent.what_i_see && (
                        <div className="mb-4">
                          <p className="text-sm font-medium mb-1">Visual Context</p>
                          <p className="text-sm text-muted-foreground">{intent.what_i_see}</p>
                        </div>
                      )}
                      
                      <div className="flex gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Sentiment:</span> {intent.sentiment || 'neutral'}
                        </div>
                        <div>
                          <span className="text-muted-foreground">Urgency:</span> {intent.urgency || 'normal'}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="events" className="h-full p-4">
            <ScrollArea className="h-full">
              <div className="space-y-2">
                {sessionData.trackedEvents.map((event: any, i: number) => (
                  <div key={i} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <MousePointer className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{event.event_name}</span>
                      </div>
                      <Badge variant={event.intent_level > 80 ? "destructive" : event.intent_level > 50 ? "default" : "secondary"}>
                        Intent: {event.intent_level}
                      </Badge>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      <p>{event.action} on {event.object}</p>
                      <p className="text-xs mt-1">{event.page_url}</p>
                      <p className="text-xs">{format(new Date(event.timestamp), 'HH:mm:ss')}</p>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="chat" className="h-full p-4">
            <ScrollArea className="h-full">
              <div className="space-y-4">
                {sessionData.chatInteractions.map((chat: any, i: number) => (
                  <Card key={i}>
                    <CardContent className="pt-6">
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm font-medium">User</p>
                          <p className="text-sm text-muted-foreground">{chat.customer_message}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium">Assistant</p>
                          <p className="text-sm text-muted-foreground">{chat.reply}</p>
                        </div>
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>{chat.page_title || chat.page_url}</span>
                          <span>{format(new Date(chat.created_at), 'HH:mm:ss')}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="pages" className="h-full p-4">
            <ScrollArea className="h-full">
              <div className="space-y-4">
                {sessionData.pages.map((page: any, i: number) => (
                  <Card key={i}>
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium">{page.title || 'Untitled'}</h4>
                          <p className="text-sm text-muted-foreground">{page.url}</p>
                          
                          {page.page_roles && page.page_roles.length > 0 && (
                            <div className="flex gap-2 mt-2">
                              {page.page_roles.map((role: any, j: number) => (
                                <Badge key={j} variant="outline">
                                  {role.role} ({Math.round(role.confidence * 100)}%)
                                </Badge>
                              ))}
                            </div>
                          )}
                          
                          <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                            <span>{page.interaction_count} interactions</span>
                            <span>First visit: {format(new Date(page.first_visited), 'HH:mm:ss')}</span>
                          </div>
                        </div>
                        
                        {page.screenshot_url && (
                          <img
                            src={page.screenshot_url}
                            alt={page.title}
                            className="w-24 h-16 object-cover rounded ml-4"
                          />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="agents" className="h-full p-4">
            <ScrollArea className="h-full">
              <div className="space-y-2">
                {sessionData.agentActivity.map((activity: any, i: number) => (
                  <div key={i} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Brain className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{activity.agent_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={activity.status === 'completed' ? 'success' : activity.status === 'failed' ? 'destructive' : 'secondary'}>
                          {activity.status}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {activity.processing_time_ms}ms
                        </span>
                      </div>
                    </div>
                    
                    {activity.error_message && (
                      <p className="text-sm text-destructive">{activity.error_message}</p>
                    )}
                    
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(activity.created_at), 'HH:mm:ss.SSS')}
                    </p>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}